import React from 'react';
import {
    Show,
    TextField,
    ReferenceManyField,
    Datagrid,
    DateField,
    ChipField,
    ReferenceField,
    FunctionField,
    useListContext,
    ListContextProvider,
    Tab,
    TabbedShowLayout,
} from 'react-admin';
import { Table, TableBody, TableRow, TableCell } from '@material-ui/core';
import CountryField from '../components/CountryField';
import StorageTypeField from '../components/StorageTypeField';

const LocationStorageContent = (props: any) => {
    const listContext: any = useListContext(props);
    const { ids = [], data = {} } = listContext || {};
    const dataMap: any = data || {};
    const quantitySum = (ids as any[]).reduce((sum: number, id: any) => {
        const record: any = dataMap[id] || {};
        const qty = Number(record.qty);
        if (Number.isNaN(qty)) return sum;
        return sum + qty;
    }, 0);

    const totalSize = (ids as any[]).reduce((sum: number, id: any) => {
        const record: any = dataMap[id] || {};
        const qty = Number(record.qty);
        const size = Number((record.storage || {}).size);
        if (Number.isNaN(qty) || Number.isNaN(size)) return sum;
        return sum + qty * size;
    }, 0);

    const summaryId = '__summary__';
    const hasRows = (ids as any[]).length > 0;
    const gridIds = hasRows ? [...(ids as any[]), summaryId] : ids;
    const gridData = hasRows
        ? {
              ...dataMap,
              [summaryId]: {
                  id: 'Total',
                  qty: quantitySum,
                  totalSize,
                  __isSummary: true,
              },
          }
        : dataMap;
    const mergedListContext = {
        ...listContext,
        ids: gridIds,
        data: gridData,
    };

    return (
        <ListContextProvider value={mergedListContext}>
            <Datagrid {...props}>
                <TextField source="serial" sortable={false} />
                <TextField source="id" />
                <FunctionField
                    label="Customer Name"
                    render={record =>
                        record.__isSummary
                            ? ''
                            : record.storage?.user?.name || '-'
                    }
                />
                <FunctionField
                    label="Storage Name"
                    render={record =>
                        record.__isSummary
                            ? ''
                            : record.storage?.name ||
                              record.storage?.code ||
                              record.storage?.id ||
                              '-'
                    }
                />
                <FunctionField
                    label="Storage Type"
                    render={record =>
                        record.__isSummary ? (
                            ''
                        ) : record.storage ? (
                            <StorageTypeField record={record.storage} />
                        ) : (
                            '-'
                        )
                    }
                />
                <FunctionField
                    label="Storage Size"
                    render={record =>
                        record.__isSummary
                            ? ''
                            : record.storage?.size !== undefined
                            ? record.storage.size
                            : '-'
                    }
                />
                <TextField source="qty" label="Quantity" />
                <FunctionField
                    label="Total Size"
                    render={record =>
                        record.__isSummary
                            ? Number(record.totalSize || 0).toFixed(3)
                            : record.qty !== undefined &&
                              record.storage?.size !== undefined
                            ? record.qty * record.storage.size
                            : '-'
                    }
                />
                <FunctionField
                    label="From"
                    render={record =>
                        record.__isSummary ? (
                            ''
                        ) : (
                            <DateField
                                locales="en-IN"
                                record={record}
                                source="from"
                                showTime
                            />
                        )
                    }
                />
                <FunctionField
                    label="To"
                    render={record =>
                        record.__isSummary ? (
                            ''
                        ) : (
                            <DateField
                                locales="en-IN"
                                record={record}
                                source="to"
                                showTime
                            />
                        )
                    }
                />
                <FunctionField
                    label="Status"
                    render={record =>
                        record.__isSummary ? (
                            ''
                        ) : (
                            <ChipField record={record} source="status" />
                        )
                    }
                />
            </Datagrid>
        </ListContextProvider>
    );
};

const LocationStorageList = props => (
    <ReferenceManyField
        reference="storage-records"
        target="locationId"
        filter={{ locationId: props.record.id }}
        {...props}
    >
        <LocationStorageContent />
    </ReferenceManyField>
);

const sanitizeShowProps = ({ hasCreate, hasEdit, hasList, hasShow, ...rest }) =>
    rest;

const LocationSummary = props => {
    const { record } = props;
    return (
        <Table>
            <TableBody>
                <TableRow>
                    <TableCell>
                        <label>Country</label>
                    </TableCell>
                    <TableCell style={{ textAlign: 'right' }}>
                        <CountryField record={record} />
                    </TableCell>
                </TableRow>
                <TableRow>
                    <TableCell>
                        <label>Warehouse</label>
                    </TableCell>
                    <TableCell style={{ textAlign: 'right' }}>
                        <ReferenceField
                            record={record}
                            label="Warehouse"
                            source="warehouseId"
                            reference="warehouses"
                            basePath="/warehouses"
                            resource="warehouses"
                            link="show"
                        >
                            <TextField source="name" />
                        </ReferenceField>
                    </TableCell>
                </TableRow>
                <TableRow>
                    <TableCell>
                        <label>Path</label>
                    </TableCell>
                    <TableCell style={{ textAlign: 'right' }}>
                        {record.path}
                    </TableCell>
                </TableRow>
                <TableRow>
                    <TableCell>
                        <label>Area</label>
                    </TableCell>
                    <TableCell style={{ textAlign: 'right' }}>
                        {record.area}
                    </TableCell>
                </TableRow>
                <TableRow>
                    <TableCell>
                        <label>Aisle</label>
                    </TableCell>
                    <TableCell style={{ textAlign: 'right' }}>
                        {record.aisle}
                    </TableCell>
                </TableRow>
                <TableRow>
                    <TableCell>
                        <label>X</label>
                    </TableCell>
                    <TableCell style={{ textAlign: 'right' }}>
                        {record.x}
                    </TableCell>
                </TableRow>
                <TableRow>
                    <TableCell>
                        <label>Y</label>
                    </TableCell>
                    <TableCell style={{ textAlign: 'right' }}>
                        {record.y}
                    </TableCell>
                </TableRow>
                <TableRow>
                    <TableCell>
                        <label>Z</label>
                    </TableCell>
                    <TableCell style={{ textAlign: 'right' }}>
                        {record.z}
                    </TableCell>
                </TableRow>
                <TableRow>
                    <TableCell>
                        <label>Capacity</label>
                    </TableCell>
                    <TableCell style={{ textAlign: 'right' }}>
                        {record.capacity ?? '-'}
                    </TableCell>
                </TableRow>
                <TableRow>
                    <TableCell>
                        <label>Used Volume</label>
                    </TableCell>
                    <TableCell style={{ textAlign: 'right' }}>
                        {record.usedVolume ?? '-'}
                    </TableCell>
                </TableRow>
                <TableRow>
                    <TableCell>
                        <label>Free Volume</label>
                    </TableCell>
                    <TableCell style={{ textAlign: 'right' }}>
                        {record.freeVolume ?? '-'}
                    </TableCell>
                </TableRow>
            </TableBody>
        </Table>
    );
};

const LocationShow = props => (
    <Show {...sanitizeShowProps(props)}>
        <TabbedShowLayout>
            <Tab label="summary">
                <LocationSummary />
            </Tab>
            <Tab label="Storages">
                <LocationStorageList />
            </Tab>
        </TabbedShowLayout>
    </Show>
);

export default LocationShow;
